import { FC, useContext, useRef, useState, useMemo } from "react";
import CN from "classnames";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../../components/Button";
import { useSelector } from "react-redux";
import store from "../../store/store";
import { addArticle } from "../Articles/reducer/articleSlice";
import { addArticleImage } from "../Articles/services";
import { Oval } from "react-loader-spinner";
import RichTextEditor, {
  RichTextEditorRef,
} from "../../components/RichTextEditor/RichTextEditor";
import { addAfterLoginNotification } from "../../components/Toast/utils/notifications";
import ImageUpload, { Image } from "./plugins/ImageUpload";

interface FormValues {
  title: string;
  content: string;
  tags: string[];
  coverImage: File | null;
  // category: string;
}

export const AddProduct: React.FC<any> = () => {
  const { isLoading, addArticleRes } = useSelector(
    (state: any) => state.rootReducer?.articles
  );
  const quillRef = useRef<RichTextEditorRef | null>(null);
  const navigate = useNavigate();

  const handleImageUploadForEditor = async (formData: FormData) => {
    try {
      const res = await addArticleImage(formData);
      return res;
    } catch (err: any) {
      addAfterLoginNotification(err.errorMsg, "error");
      throw err;
    }
  };

  // Validation schema
  const validationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    content: Yup.string().required("Content is required"),
    tags: Yup.array(),
    // coverImage: Yup.mixed(),
  });

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    values,
    errors,
    touched,
  } = useFormik<FormValues>({
    initialValues: {
      title: "",
      content: "",
      tags: [],
      coverImage: null,
    },
    validationSchema,
    onSubmit: (values: FormValues) => {
      const formData = new FormData();
      if (values.coverImage) formData.append("coverImage", values.coverImage);
      formData.append("title", values.title);
      formData.append("content", values.content);
      store.dispatch(addArticle(formData));
    },
  });

  const handleImageUpload = (image: File | Image | null) => {
    setFieldValue("coverImage", image);
  };

  const Editor = useMemo(
    () => (
      <RichTextEditor
        ref={quillRef}
        value={values.content}
        onChange={(value) => setFieldValue("content", value)}
        placeholder="Write your article content here..."
        onImageUpload={handleImageUploadForEditor}
        className={CN(
          errors.content && touched.content
            ? "border-red-500"
            : "border-primary1"
        )}
      />
    ),
    [errors.content, touched.content]
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <form onSubmit={handleSubmit}>
        <div className="lg:grid grid-cols-2 flex flex-col gap-6 my-4 py-8 lg:mx-8 mx-4">
          <div className="col-span-2">
            <label className="block text-base font-semibold mb-2">
              Banner <span className="text-red-500">*</span>
            </label>
            <ImageUpload onUpload={handleImageUpload} />
            {errors.coverImage && touched.coverImage && (
              <p className="text-red-500 text-sm my-2">{errors.coverImage}</p>
            )}
          </div>
          {/* Product Name */}
          <div className="col-span-2">
            <label className="block text-base font-semibold mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={values.title}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Untitled product"
              className={CN(
                "block w-full p-3 border rounded-md",
                errors.title && touched.title
                  ? "border-red-500"
                  : "border-primary1"
              )}
            />
            {errors.title && touched.title && (
              <p className="text-red-500 text-sm my-2">{errors.title}</p>
            )}
          </div>
          <div className="col-span-2">
            <label className="block text-base font-semibold mb-2">
              Article Content <span className="text-red-500">*</span>
            </label>
            <div className="">{Editor}</div>
            {errors.content && touched.content && (
              <p className="text-red-500 text-sm my-2">{errors.content}</p>
            )}
          </div>
        </div>

        {/* Buttons */}
        <div
          className={`flex justify-end flex-row gap-4 mt-5 py-4 w-full sticky bottom-0 bg-primary2 shadow-tableShadow lg:px-8 px-4`}
        >
          <div className="col-span-2 flex justify-end gap-5">
            <Button onClick={() => navigate("/articles")} apparence="gray">
              Cancel
            </Button>

            <Button
              isLoading={isLoading.articleAdd}
              view="default"
              type="submit"
            >
              <div className="flex gap-1">
                {isLoading.articleAdd && (
                  <Oval
                    height={18}
                    width={18}
                    color="#ffff"
                    wrapperStyle={{}}
                    wrapperClass="max-sm:mt-[3.5px] mt-[5.2px] max-lg:mt-[5.2px]"
                    visible={true}
                    ariaLabel="oval-loading"
                    secondaryColor="gray-primary"
                    strokeWidth={6}
                    strokeWidthSecondary={6}
                  />
                )}
                <div className="font-normal text-base">Save</div>
              </div>
            </Button>
          </div>
        </div>
      </form>
    </motion.div>
  );
};

export default AddProduct;
